import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface BillingPlan {
  id: string;
  name: string;
  priceMonthlyCents: number | null;
  recordLimit: number | null;
  seatLimit: number | null;
  selfServe: boolean;
}

export interface BillingPlansResponse {
  plans: BillingPlan[];
}

export interface BillingSubscriptionResponse {
  plan: BillingPlan;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  recordUsage: { used: number; limit: number };
  seatUsage: { used: number; limit: number };
}

export interface CheckoutSessionRequest {
  type: 'subscription';
  planId?: string;
  successPath?: string;
  cancelPath?: string;
}

export interface CheckoutSessionResponse {
  url: string;
}

export interface PortalSessionResponse {
  url: string;
}

/**
 * Wraps all /api/billing/* calls. Shared by the pricing page (public), the
 * signup wizard, and the billing settings page (authenticated) so plan
 * data and checkout logic are never duplicated across them.
 */
@Injectable({
  providedIn: 'root',
})
export class BillingService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  /** Public - no auth required. Used by the pricing page and signup wizard. */
  getPlans(): Observable<BillingPlansResponse> {
    return this.http.get<BillingPlansResponse>(`${this.apiUrl}/billing/plans`);
  }

  /** Authenticated. Current org's plan, status, and usage vs. limits. */
  getSubscription(): Observable<BillingSubscriptionResponse> {
    return this.http.get<BillingSubscriptionResponse>(`${this.apiUrl}/billing/subscription`);
  }

  /** Authenticated. Returns a Stripe Checkout URL to redirect the browser to. */
  createCheckoutSession(request: CheckoutSessionRequest): Observable<CheckoutSessionResponse> {
    return this.http.post<CheckoutSessionResponse>(`${this.apiUrl}/billing/checkout-session`, request);
  }

  /** Authenticated. Returns a Stripe Billing Portal URL to redirect the browser to. */
  createPortalSession(): Observable<PortalSessionResponse> {
    return this.http.post<PortalSessionResponse>(`${this.apiUrl}/billing/portal-session`, {});
  }
}
